export const COMM_STATUS = [
  { value: 'no_phone', label: 'Sem telefone', receives: false },
  { value: 'not_sent', label: 'Aguardando primeira mensagem', receives: false },
  { value: 'pending', label: 'Aguardando confirmação', receives: true },
  { value: 'confirmed', label: 'Confirmado', receives: true },
  { value: 'opted_out', label: 'Desligado pelo usuário', receives: false },
  { value: 'wrong_number', label: 'Número errado', receives: false },
  { value: 'no_whatsapp', label: 'Número sem WhatsApp', receives: false },
] as const;

export type CommStatus = (typeof COMM_STATUS)[number]['value'];

export const commStatusLabel = (s: string) => COMM_STATUS.find((x) => x.value === s)?.label ?? s;

/** Formats a normalized phone (55 + DDD + number) as +55 (11) 98765-4321. */
export function formatCommPhone(p?: string | null): string {
  if (!p) return '—';
  const d = p.replace(/\D/g, '');
  if (!/^55\d{10,11}$/.test(d)) return p;
  const ddd = d.slice(2, 4);
  const rest = d.slice(4);
  const cut = rest.length === 9 ? 5 : 4;
  return `+55 (${ddd}) ${rest.slice(0, cut)}-${rest.slice(cut)}`;
}

/** Estimated duration for N messages at the average interval, in pt-BR. */
export function estimateDuration(minS: number, maxS: number, count = 100): string {
  const totalS = Math.round(count * ((minS + maxS) / 2));
  const h = Math.floor(totalS / 3600);
  const m = Math.round((totalS % 3600) / 60);
  if (h === 0) return `${Math.max(m, 1)} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export interface CommSettingsInput {
  min_interval_s: number;
  max_interval_s: number;
  urgent_start: string;
  urgent_end: string;
  batch_start: string;
  batch_end: string;
  support_start: string;
  support_end: string;
}

const t = (v: string) => (v || '').slice(0, 5);

export function validateCommSettings(s: CommSettingsInput): string[] {
  const errors: string[] = [];
  if (!(s.min_interval_s >= 5)) errors.push('O intervalo mínimo deve ser de pelo menos 5 segundos.');
  if (!(s.min_interval_s <= s.max_interval_s)) errors.push('O intervalo mínimo não pode ser maior que o máximo.');
  if (!(t(s.urgent_start) < t(s.urgent_end))) errors.push('Na fila urgente, o início deve ser antes do fim.');
  if (!(t(s.batch_start) < t(s.batch_end))) errors.push('No lote, o início deve ser antes do fim.');
  if (!(t(s.support_start) < t(s.support_end))) errors.push('No horário de atendimento, o início deve ser antes do fim.');
  return errors;
}

export const COMM_CONNECTION = [
  { value: 'not_configured', label: 'Não configurado' },
  { value: 'disconnected', label: 'Desconectado' },
  { value: 'connecting', label: 'Conectando' },
  { value: 'qr', label: 'Aguardando QR Code' },
  { value: 'connected', label: 'Conectado' },
] as const;

export const commConnectionLabel = (s?: string | null) => COMM_CONNECTION.find((x) => x.value === s)?.label ?? 'Não configurado';

/** The number is "down" only after the instance exists and it is not connected. */
export const isCommNumberDown = (s?: string | null) => !!s && s !== 'not_configured' && s !== 'connected';

export const fmtHHMM = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }) : '—';

/** Masks a normalized phone keeping DDD, first digit and last 4: +55 85 9••••-7392. */
export function maskCommPhone(p?: string | null): string {
  if (!p) return '—';
  const d = p.replace(/\D/g, '');
  if (!/^55\d{10,11}$/.test(d)) return '••••' + d.slice(-4);
  const rest = d.slice(4);
  return `+55 ${d.slice(2, 4)} ${rest[0]}••••-${rest.slice(-4)}`;
}

export const OUTBOX_STATUS = [
  { value: 'queued', label: 'Na fila' },
  { value: 'sending', label: 'Enviando' },
  { value: 'sent', label: 'Enviada' },
  { value: 'failed', label: 'Falhou' },
  { value: 'canceled', label: 'Cancelada' },
  { value: 'skipped', label: 'Ignorada' },
] as const;
export const outboxStatusLabel = (s: string) => OUTBOX_STATUS.find((x) => x.value === s)?.label ?? s;

export const OUTBOX_KIND = [
  { value: 'test', label: 'Teste' },
  { value: 'first_message', label: 'Primeira mensagem' },
  { value: 'notification', label: 'Notificação' },
  { value: 'auto_reply', label: 'Resposta automática' },
  { value: 'manual', label: 'Manual' },
  { value: 'internal', label: 'Interna' },
  { value: 'wave', label: 'Onda' },
  { value: 'broadcast', label: 'Comunicado' },
  { value: 'digest', label: 'Resumo' },
] as const;
export const outboxKindLabel = (s: string) => OUTBOX_KIND.find((x) => x.value === s)?.label ?? s;
export const laneLabel = (s: string) => (s === 'urgent' ? 'Urgente' : s === 'batch' ? 'Lote' : s);

/** Only failed messages can be resent; only queued ones can be canceled. */
export const canResend = (status: string) => status === 'failed';
export const canCancel = (status: string) => status === 'queued';

/** Seconds until the next send slot (never negative). */
export function secondsUntil(iso?: string | null, now = Date.now()): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 1000));
}

export const fmtHHMMSS = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

// ---- Stage 4: conversations ----
export const CONSENT_ACTION: Record<string, string> = {
  sent: 'Primeira mensagem enviada', yes: 'SIM', no: 'NÃO', wrong_number: 'Não sou', stop: 'PARAR', activate: 'ATIVAR',
  profile_on: 'Ligou no perfil', profile_off: 'Desligou no perfil', category_change: 'Mudou categorias', phone_changed: 'Telefone alterado',
};
export const consentActionLabel = (a: string) => CONSENT_ACTION[a] ?? a;
export const CONSENT_SOURCE: Record<string, string> = {
  button: 'Botão', poll: 'Enquete', text: 'Texto', profile: 'Perfil', system: 'Sistema', admin: 'SuperAdmin',
};
export const consentSourceLabel = (s: string) => CONSENT_SOURCE[s] ?? s;

/** "Resposta: SIM → Confirmado" */
export const keywordLabel = (action: string, status: string) => `Resposta: ${consentActionLabel(action)} → ${commStatusLabel(status)}`;

const MEDIA_LABEL: Record<string, string> = {
  image: 'Imagem', audio: 'Áudio', video: 'Vídeo', document: 'Documento', sticker: 'Figurinha', location: 'Localização',
};
export const mediaLabel = (t?: string | null) => (t && MEDIA_LABEL[t]) || null;

const TEMPLATE_LABEL: Record<string, string> = {
  reply_yes: 'resposta SIM', reply_no: 'resposta NÃO', reply_wrong: 'resposta Não sou', reply_activate: 'resposta ATIVAR',
  away_message: 'fora do horário', unknown_number: 'número desconhecido', test: 'teste',
  internal_network_created: 'rede nova cadastrada', internal_invoice_overdue: 'cliente com fatura vencida', internal_daily_sweep_failed: 'falha na verificação diária',
};
export const templateLabel = (key?: string | null, kind?: string | null) =>
  (key && TEMPLATE_LABEL[key]) || (kind ? outboxKindLabel(kind).toLowerCase() : 'sistema');

export interface CommConversationRow {
  id: string; phone: string; user_ids: string[]; status: string; unread_count: number;
  last_message_at: string | null; last_message_preview: string | null;
  user_names: string[]; network_ids: string[]; network_names: string[]; has_inbound: boolean;
}
export interface CommConversationFilter { status: 'open' | 'resolved' | 'all'; who: 'all' | 'users' | 'unknown'; networkId: string; search: string }

/** Unread first, then most recent; applies status/who/network filters and name/phone search. */
export function filterConversations(rows: CommConversationRow[], f: CommConversationFilter): CommConversationRow[] {
  const q = f.search.trim().toLowerCase();
  const qd = q.replace(/\D/g, '');
  return rows
    .filter((r) => f.status === 'all' || r.status === f.status)
    .filter((r) => f.who === 'all' || (f.who === 'unknown' ? r.user_ids.length === 0 : r.user_ids.length > 0))
    .filter((r) => !f.networkId || r.network_ids.includes(f.networkId))
    .filter((r) => !q || r.user_names.some((n) => n.toLowerCase().includes(q)) || (qd.length >= 3 && r.phone.includes(qd)))
    .sort((a, b) => (Number(b.unread_count > 0) - Number(a.unread_count > 0))
      || (new Date(b.last_message_at ?? 0).getTime() - new Date(a.last_message_at ?? 0).getTime()));
}

/** Text shown to the user in "Avisos pelo WhatsApp" for their own status. */
export function myWhatsAppStatusText(status: string, optedOutAt?: string | null): string {
  switch (status) {
    case 'confirmed': return 'Ligado · confirmado';
    case 'pending': return 'Ligado · aguardando sua confirmação no WhatsApp';
    case 'not_sent': return 'Ligado · você vai receber uma mensagem de confirmação';
    case 'opted_out': {
      const d = optedOutAt ? new Date(optedOutAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' }) : '';
      return d ? `Desligado por você em ${d}` : 'Desligado por você';
    }
    case 'wrong_number': return 'Este número foi marcado como errado. Corrija o telefone';
    case 'no_whatsapp': return 'Este número não tem WhatsApp';
    default: return 'Cadastre seu WhatsApp para receber avisos';
  }
}

/** Business days of batch sending a wave needs (wave limit capped by the batch daily limit). */
export function estimateWaveDays(count: number, waveLimit: number, batchLimit?: number | null): number {
  const perDay = Math.max(1, Math.min(waveLimit || 1, batchLimit && batchLimit > 0 ? batchLimit : Infinity));
  return count > 0 ? Math.ceil(count / perDay) : 0;
}
